import { useState, type ReactNode } from "react";
import {
  navegar,
  puedeAbrir,
  seccionDeRuta,
  PILDORAS,
  PUNTO_DE_VENTA,
  SECCIONES,
  TABS_INVENTARIO,
  VISTAS,
  RUTAS_POS,
  type RutaId,
  type Vista,
} from "../lib/ruta";
import { mutar, useApi } from "../lib/useApi";
import { avisarCambioDelMapa } from "../lib/avisos";
import { useMasAngostoQue, UMBRAL_FILA_PX } from "../lib/pantalla";
import { Card, Chip, SectionLabel, Tabs, TabPill, useToast, cn } from "../components/ui";
import type { SesionActiva, Rol } from "../lib/tipos";

// Mapa del sistema (#/mapa): el "¿dónde está qué?". Se deriva EN VIVO de SECCIONES + VISTAS de
// ruta.ts — una pantalla nueva aparece sola aquí. El Record<RutaId, string> de abajo es EXHAUSTIVO
// a propósito: agregar una ruta sin escribirle su línea de "para qué sirve" no compila.
//
// Mapa del tesoro (cadena huayruro-orden-y-mapa, tramo 05): el DUEÑO ve además «¿Qué está listo y qué
// falta?» — los 13 pasos de la puesta en marcha, lo que se marca a mano y el estado de cada pantalla.
// Todo eso lo arma el servidor en GET /mapa/estado, que solo le responde al Dueño: esta pantalla ni
// siquiera lo pide para los demás roles (M-7), y la lista de lo que falta construir no viaja en el
// paquete de la app (OM-11).

const PARA_QUE: Record<RutaId, string> = {
  hoy: "El resumen del día: qué necesita tu atención y cómo va cada botica.",
  mostrador: "Donde se cobra: buscar producto, carrito y cobro — con o sin internet.",
  recepcion: "Registrar la mercadería que llega de la droguería (entra al stock con su lote).",
  clientes: "Saber quién compra y qué seguimiento le toca.",
  reconocimiento: "Quién entró a la botica (la webcam sugiere, quien atiende confirma), quién tiene su cara registrada con permiso, y retirar ese permiso.",
  reposiciones: "Que el crónico no se quede sin su medicina — y vuelva a comprar aquí: a quién escribirle hoy y el WhatsApp ya escrito.",
  sugerencias: "Cada venta puede sumar un gustito con consejo profesional: qué se recomienda, con qué frase, y cuántas se aceptan.",
  copiloto: "Lo que el panel del mostrador le recuerda a quien atiende: qué preguntar antes de vender y cómo se usa cada principio activo.",
  inventario: "El stock de cada botica, en pestañas: por vencer, stock bajo, faltantes, conteo y recepciones por aprobar.",
  "por-vencer": "Los lotes que vencen en los próximos 90 días, para moverlos antes de perderlos.",
  "stock-bajo": "Los productos por debajo de su mínimo, con cuántos días alcanzan al ritmo de venta.",
  caja: "El cierre del día: cuánto debía haber en caja, cuánto se contó y la diferencia.",
  dashboard: "Ventas en vivo, cierres de caja y últimas ventas, por botica o de toda la cadena.",
  casos: "La bandeja de alertas del sistema (descuadres, anulaciones, mermas). Tú revisas y decides; nada es automático.",
  conteo: "Conteo de inventario: hojas ciegas por prioridad para verificar que el stock del sistema sea el real.",
  faltantes: "Lo que falta reponer en la botica: stock bajo el mínimo o lo que pidieron en el mostrador y no había.",
  consolidado: "La única vista cruzada de la cadena: ventas por botica y faltantes consolidados para pedir.",
  catalogo: "Los productos que vendes: altas asistidas desde el catálogo nacional, presentaciones y precios.",
  "importar-catalogo": "Cargar el catálogo completo desde una hoja CSV, con previsualización antes de escribir.",
  "catalogo-prueba": "Catálogo de práctica desde el maestro nacional, para que el oído tenga contra qué comparar.",
  proveedores: "Tus droguerías y sus listas de precios: subir o pegar la lista y cruzarla con tu catálogo.",
  pedido: "El pedido sugerido de la semana: compara droguerías y arma la orden más barata en 1–2 droguerías.",
  "recepciones-pendientes": "Lo que el bot de Telegram registró y espera aprobación para entrar al stock.",
  grabadores: "Los teléfonos que graban en el mostrador: el token de cada uno y el botón para apagarlo.",
  "audio-calidad": "La salud del oído del mostrador: señales reconocidas y el diccionario de nombres que aprende.",
  usuarios: "Quién entra al sistema y con qué rol.",
  sucursales: "Tus boticas: nombre, dirección, si están activas y si ya abrieron hoy.",
  ajustes: "La trastienda: usuarios, boticas, sugerencias, grabadores y el oído, todo en un lugar.",
  mapa: "Esta pantalla: el plano de todo el sistema.",
  ingreso: "Cargar el inventario repisa por repisa con el lector: el borrador se guarda solo y «Subir repisa» lo pasa al stock.",
  "mi-cuenta": "La cuenta de quien vende: su examen del día, su plan, sus notas, las fichas aprobadas y su contraseña.",
  examen: "El examen diario de 10 preguntas sobre lo que esa persona cargó hoy (aprueba con 10 de 12).",
  aprendizaje: "Las notas del examen diario por persona y día, el plan del día y las fichas de medicamentos por aprobar.",
};

const ROL_NOMBRE: { rol: Rol; label: string }[] = [
  { rol: "super_admin", label: "Dueño" },
  { rol: "admin_sucursal", label: "Encargado" },
  { rol: "operador", label: "Vendedor" },
  { rol: "lector_reportes", label: "Lector" },
];

function vistaDe(id: RutaId): Vista {
  // VISTAS registra todas las rutas; si esto lanzara, ruta.ts está roto (owns apunta a una ruta inexistente).
  const v = VISTAS.find((x) => x.id === id);
  if (!v) throw new Error(`ruta sin vista: ${id}`);
  return v;
}

const enLista = (xs: string[]) => (xs.length <= 1 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} y ${xs[xs.length - 1]}`);

// «La ven: Dueño y Encargado» (LEC 5.1 regla 8: texto de 13 px en vez de chips de 10.5).
function laVen(v: Vista): string {
  const quienes = ROL_NOMBRE.filter((r) => v.roles.includes(r.rol)).map((r) => r.label);
  return quienes.length === ROL_NOMBRE.length ? "La ven todos" : `La ${quienes.length > 1 ? "ven" : "ve"}: ${enLista(quienes)}`;
}

// «Llegas por…» (ARQ 3.6): cómo se abre cada pantalla desde el menú de hoy.
function llegasPor(id: RutaId): string {
  if (SECCIONES.some((s) => s.ruta === id)) return "el menú";
  if (TABS_INVENTARIO.some((t) => t.ruta === id)) return "una pestaña de Inventario";
  const pildora = PILDORAS.find((p) => p.rutas.includes(id));
  if (pildora) return `una píldora de «${seccionDeRuta(pildora.rutas[0]!)?.label ?? ""}»`;
  if (PUNTO_DE_VENTA.includes(id)) return "el menú, en «Punto de venta»";
  if (seccionDeRuta(id)?.id === "ajustes") return "Ajustes";
  return "su dirección";
}

// ---------- Lo que responde GET /mapa/estado (solo el Dueño) ----------
type EstadoMapa = "ok" | "toca";
type TipoLinea = "toca" | "hecho" | "hito" | "construir";
type DetalleBotica = { nombre: string; ok: boolean; nota: string };
type PasoMapa = {
  n: number;
  clave: string;
  bloque: "abrir" | "vender";
  titulo: string;
  ruta: string | null;
  estado: EstadoMapa;
  detalle: string;
  quien: string;
  ir: boolean;
  boticas: DetalleBotica[] | null;
};
type MarcaMapa = {
  clave: string;
  titulo: string;
  ayuda: string;
  alcance: "botica" | "negocio";
  admite_no_aplica: boolean;
  estado: EstadoMapa;
  detalle: string;
  boticas: { sucursal_id: string; nombre: string; valor: string | null }[] | null;
  valor: string | null;
};
type PantallaMapa = { ruta: string; estado: EstadoMapa; lineas: { tipo: TipoLinea; texto: string }[] };
type MapaEstado = {
  hoy: string;
  resumen: { ya_estan: number; te_tocan: number; faltan_construir: number };
  boticas: { sucursal_id: string; nombre: string; apertura: string | null }[];
  pasos: PasoMapa[];
  marcas: MarcaMapa[];
  pantallas: PantallaMapa[];
  falta_construir: { ruta: string | null; texto: string }[];
};

type Filtro = "todo" | "toca" | "construir";

// M-4: el emoji nunca va solo, siempre con sus palabras. ✅ ⭐ 🛠️ quedan reservados para el estado.
// Propia y no <Chip>: la del sistema es de 11.5 px y el Mapa se lee con 13 px o más (M-9); `cn` solo
// junta clases, así que un text-[13px] encima de la de Chip no le ganaría.
function ChipEstado({ estado, deOtro = false }: { estado: EstadoMapa; deOtro?: boolean }) {
  return (
    <span className={cn("inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[13px] font-semibold", estado === "ok" ? "bg-ok-soft text-ok" : "bg-warn-soft text-warn")}>
      {estado === "ok" ? "✅ Ya está" : deOtro ? "⭐ Le toca a tu equipo" : "⭐ Te toca a ti"}
    </span>
  );
}

const MARCA_LINEA: Record<TipoLinea, string> = { toca: "⭐", hecho: "✅", hito: "·", construir: "🛠️" };

function Lineas({ lineas }: { lineas: { tipo: TipoLinea; texto: string }[] }) {
  if (lineas.length === 0) return null;
  return (
    <ul className="mt-1.5 flex flex-col gap-1">
      {lineas.map((l, i) => (
        <li key={i} className="flex gap-2 text-[13px] leading-snug text-ink-2">
          <span aria-hidden="true" className="w-[18px] flex-none text-center">{MARCA_LINEA[l.tipo]}</span>
          <span className={cn(l.tipo === "toca" && "font-semibold text-ink")}>{l.texto}</span>
        </li>
      ))}
    </ul>
  );
}

// «Ir →» de 44 px de alto (LEC 4.5); a lo ancho en el celular.
function IrBoton({ id, etiqueta, className }: { id: RutaId; etiqueta: string; className?: string }) {
  return (
    <button
      type="button"
      onClick={() => navegar(id)}
      aria-label={`Ir a ${etiqueta}`}
      className={cn(
        "inline-flex min-h-[44px] flex-none items-center justify-center rounded-[9px] border border-line-input bg-card px-4 text-[13px] font-semibold text-link transition-colors hover:bg-hover-btn hover:text-link-hover",
        className,
      )}
    >
      Ir →
    </button>
  );
}

// ---------- Bloque del Dueño: ¿Qué está listo y qué falta? ----------
function FilaPaso({ p, hayAperturaPendiente }: { p: PasoMapa; hayAperturaPendiente: boolean }) {
  const ruta = p.ruta as RutaId | null;
  const conIr = p.ir && ruta !== null && puedeAbrir("super_admin", ruta);
  const deOtro = !p.ir && p.ruta !== null;
  const pideFecha = p.estado === "toca" && (p.clave === "venta" || p.clave === "cierre") && hayAperturaPendiente;
  const irAMarcas = p.estado === "toca" && (p.clave === "equipo" || pideFecha);
  return (
    <li className="grid grid-cols-[28px_1fr] gap-x-2 border-b border-line-row py-3 last:border-0 sm:grid-cols-[28px_1fr_auto] sm:gap-x-3">
      <span className="pt-0.5 text-[13px] font-semibold tabular-nums text-ink-3">{p.n}</span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-[15px] font-semibold text-ink">{p.titulo}</span>
          <ChipEstado estado={p.estado} deOtro={deOtro} />
        </div>
        <p className="mt-1 text-[13px] leading-relaxed text-ink-2">{p.detalle}</p>
        <p className="mt-0.5 text-[13px] text-ink-3">{p.quien}</p>
        {p.boticas && p.boticas.length > 1 && (
          <ul className="mt-1.5 flex flex-col gap-0.5 sm:flex-row sm:flex-wrap sm:gap-x-4">
            {p.boticas.map((b) => (
              <li key={b.nombre} className="text-[13px] text-ink-2">
                <span aria-hidden="true" className={cn("mr-1", b.ok ? "text-ok" : "text-ink-3")}>{b.ok ? "✓" : "○"}</span>
                {b.nombre}: {b.nota}
              </li>
            ))}
          </ul>
        )}
      </div>
      {conIr && ruta ? (
        <IrBoton id={ruta} etiqueta={p.titulo} className="col-span-2 mt-2 w-full sm:col-span-1 sm:mt-0 sm:w-auto sm:self-start" />
      ) : irAMarcas ? (
        // Botón y no enlace: en esta app el «#» de la dirección elige la pantalla (#marcas sacaría del Mapa).
        <button
          type="button"
          onClick={() => {
            const d = document.getElementById("marcas");
            if (d instanceof HTMLDetailsElement) d.open = true;
            d?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className="col-span-2 mt-2 inline-flex min-h-[44px] items-center justify-center whitespace-nowrap rounded-[9px] border border-line-input bg-card px-4 text-[13px] font-semibold text-link hover:bg-hover-btn sm:col-span-1 sm:mt-0 sm:self-start"
        >
          {pideFecha ? "Anotar la apertura ↓" : "Marcar ↓"}
        </button>
      ) : null}
    </li>
  );
}

// Botones de tres posiciones (44 px): Listo · No aplica · Sin marcar.
function Opciones({ valor, admiteNoAplica, ocupado, onElegir, etiqueta }: { valor: string | null; admiteNoAplica: boolean; ocupado: boolean; onElegir: (v: string | null) => void; etiqueta: string }) {
  const ops: { v: string | null; t: string }[] = [{ v: "listo", t: admiteNoAplica ? "Listo" : "Revisado" }, ...(admiteNoAplica ? [{ v: "no_aplica", t: "No aplica" }] : []), { v: null, t: "Sin marcar" }];
  return (
    <div role="group" aria-label={etiqueta} className={cn("grid gap-1.5", admiteNoAplica ? "grid-cols-3" : "grid-cols-2")}>
      {ops.map((o) => {
        const activo = valor === o.v;
        return (
          <button
            key={o.t}
            type="button"
            aria-pressed={activo}
            disabled={ocupado || activo}
            onClick={() => onElegir(o.v)}
            className={cn(
              "min-h-[44px] whitespace-nowrap rounded-[9px] border px-3 text-[13px] font-semibold transition-colors disabled:cursor-default",
              // Lo elegido se nota sin gritar: «Listo» en verde, «No aplica» en gris y «Sin marcar» apenas con
              // un borde punteado (en la primera versión el negro de «Sin marcar» se comía la sección).
              !activo
                ? "border-line-input bg-card text-nav-inactive hover:bg-hover-btn disabled:opacity-60"
                : o.v === "listo"
                  ? "border-ok bg-ok-soft text-ok"
                  : o.v === "no_aplica"
                    ? "border-line-input bg-inset text-ink"
                    : "border-dashed border-line-input bg-transparent text-ink-3",
            )}
          >
            {o.t}
          </button>
        );
      })}
    </div>
  );
}

function FechaApertura({ b, hoy, onCambio }: { b: MapaEstado["boticas"][number]; hoy: string; onCambio: () => void }) {
  const toast = useToast();
  const [fecha, setFecha] = useState(b.apertura ?? "");
  const [ocupado, setOcupado] = useState(false);
  const guardar = async (valor: string | null) => {
    setOcupado(true);
    try {
      if (valor === null) await mutar(`/mapa/marcas?clave=apertura&sucursal_id=${encodeURIComponent(b.sucursal_id)}`, { method: "DELETE" });
      else await mutar("/mapa/marcas", { method: "PUT", body: { clave: "apertura", sucursal_id: b.sucursal_id, valor } });
      toast(valor === null ? `Se quitó la fecha de apertura de ${b.nombre}.` : `${b.nombre}: abrió el ${valor}.`);
      if (valor === null) setFecha("");
      onCambio();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  };
  return (
    <li className="flex flex-col gap-1.5 border-b border-line-row py-2.5 last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      <span className="text-[13px] font-semibold text-ink">{b.nombre}</span>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          value={fecha}
          max={hoy}
          onChange={(e) => setFecha(e.target.value)}
          aria-label={`Fecha de apertura de ${b.nombre}`}
          className="min-h-[44px] rounded-[9px] border border-line-input bg-field px-2.5 text-[13px] tabular-nums text-ink outline-none"
        />
        <button
          type="button"
          disabled={ocupado || !fecha || fecha === b.apertura}
          onClick={() => void guardar(fecha)}
          className="min-h-[44px] rounded-[9px] border border-line-input bg-card px-4 text-[13px] font-semibold text-link hover:bg-hover-btn disabled:opacity-50"
        >
          Guardar
        </button>
        {b.apertura && (
          <button type="button" disabled={ocupado} onClick={() => void guardar(null)} className="min-h-[44px] px-2 text-[13px] text-ink-3 underline hover:text-accent-ink disabled:opacity-50">
            Quitar
          </button>
        )}
      </div>
    </li>
  );
}

function MarcaAMano({ m, onCambio }: { m: MarcaMapa; onCambio: () => void }) {
  const toast = useToast();
  const [ocupado, setOcupado] = useState(false);
  const elegir = async (sucursalId: string | null, nombre: string, valor: string | null) => {
    setOcupado(true);
    try {
      if (valor === null) {
        const suc = sucursalId ? `&sucursal_id=${encodeURIComponent(sucursalId)}` : "";
        await mutar(`/mapa/marcas?clave=${m.clave}${suc}`, { method: "DELETE" });
      } else {
        await mutar("/mapa/marcas", { method: "PUT", body: { clave: m.clave, sucursal_id: sucursalId, valor } });
      }
      toast(`${m.titulo} · ${nombre}: ${valor === "listo" ? "listo" : valor === "no_aplica" ? "no aplica" : "sin marcar"}.`);
      onCambio();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  };
  return (
    <div className="border-t border-line-row py-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-[15px] font-semibold text-ink">{m.titulo}</span>
        <ChipEstado estado={m.estado} />
      </div>
      <p className="mt-0.5 text-[13px] leading-relaxed text-ink-2">{m.ayuda}</p>
      <p className="mt-0.5 text-[13px] text-ink-3">{m.detalle}</p>
      <ul className="mt-2 flex flex-col gap-2">
        {m.boticas ? (
          m.boticas.map((b) => (
            <li key={b.sucursal_id} className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
              <span className="text-[13px] font-semibold text-ink">{b.nombre}</span>
              <Opciones valor={b.valor} admiteNoAplica={m.admite_no_aplica} ocupado={ocupado} etiqueta={`${m.titulo} en ${b.nombre}`} onElegir={(v) => void elegir(b.sucursal_id, b.nombre, v)} />
            </li>
          ))
        ) : (
          <li className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <span className="text-[13px] font-semibold text-ink">Todo el negocio</span>
            <Opciones valor={m.valor} admiteNoAplica={m.admite_no_aplica} ocupado={ocupado} etiqueta={m.titulo} onElegir={(v) => void elegir(null, "todo el negocio", v)} />
          </li>
        )}
      </ul>
    </div>
  );
}

// <details> con su resumen de 44 px. En el celular solo «Para abrir» llega desplegado (6.6).
function Plegable({ id, titulo, cta, abierto, children }: { id?: string; titulo: string; cta: string; abierto: boolean; children: ReactNode }) {
  return (
    <details id={id} open={abierto} className="group rounded-[12px] border border-line bg-card px-4 py-1 sm:px-5">
      <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
        <span className="text-[15px] font-bold text-ink">{titulo}</span>
        <span className="flex items-center gap-2 whitespace-nowrap text-[13px] text-ink-3">
          {cta}
          <span aria-hidden="true" className="transition-transform group-open:rotate-180">▾</span>
        </span>
      </summary>
      <div className="pb-3">{children}</div>
    </details>
  );
}

function BloqueTesoro({ e, onCambio }: { e: MapaEstado; onCambio: () => void }) {
  const angosto = useMasAngostoQue(UMBRAL_FILA_PX);
  const { ya_estan, te_tocan, faltan_construir } = e.resumen;
  const total = ya_estan + te_tocan;
  const abrir = e.pasos.filter((p) => p.bloque === "abrir");
  const vender = e.pasos.filter((p) => p.bloque === "vender");
  const hayAperturaPendiente = e.boticas.some((b) => !b.apertura);
  const tocaVender = vender.filter((p) => p.estado === "toca").length;
  const marcasPendientes = e.marcas.filter((m) => m.estado === "toca").length;
  const apertura = e.marcas.find((m) => m.clave === "apertura");
  return (
    <section aria-label="¿Qué está listo y qué falta?" className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <SectionLabel>Solo lo ves tú</SectionLabel>
        <h2 className="text-[17px] font-bold tracking-[-0.01em] text-ink">¿Qué está listo y qué falta?</h2>
        <p className="text-[13px] leading-relaxed text-ink-2" data-testid="mapa-resumen">
          <span className="font-semibold text-ink">✅ {ya_estan} ya están</span> · <span className="font-semibold text-ink">⭐ {te_tocan} te tocan</span> ·{" "}
          <span className="font-semibold text-ink">🛠️ {faltan_construir} por construir</span>
        </p>
        <div
          className="flex h-2 overflow-hidden rounded-full border border-line bg-inset"
          role="img"
          aria-label={`${ya_estan} de ${total} tareas de la puesta en marcha ya están`}
        >
          <i className="block h-full bg-ok-strong" style={{ width: `${total ? (100 * ya_estan) / total : 0}%` }} />
          <i className="block h-full bg-warn-dot" style={{ width: `${total ? (100 * te_tocan) / total : 0}%` }} />
        </div>
      </div>

      <div className="rounded-[12px] border border-warn-dot bg-card px-4 py-3 shadow-[0_0_0_3px_var(--color-warn-soft)] sm:px-5">
        <h3 className="text-[15px] font-bold text-ink">Para abrir</h3>
        <p className="text-[13px] text-ink-3">Sin esto no hay primera venta. Va en este orden y casi todo se marca solo.</p>
        <ul>
          {abrir.map((p) => (
            <FilaPaso key={p.n} p={p} hayAperturaPendiente={hayAperturaPendiente} />
          ))}
        </ul>
      </div>

      <Plegable titulo="Para vender mejor" cta={tocaVender ? `${tocaVender} de ${vender.length} te tocan` : `${vender.length} pasos listos`} abierto={!angosto}>
        <p className="text-[13px] text-ink-3">La botica ya vende sin esto. Cada paso suma una función.</p>
        <ul>
          {vender.map((p) => (
            <FilaPaso key={p.n} p={p} hayAperturaPendiente={hayAperturaPendiente} />
          ))}
        </ul>
      </Plegable>

      <Plegable id="marcas" titulo="Se marca a mano" cta={marcasPendientes ? `${marcasPendientes} sin marcar` : "Todo marcado"} abierto={!angosto}>
        <p className="text-[13px] text-ink-3">Ningún dato lo puede decir: lo marcas tú cuando lo tengas. «No aplica» cuenta como resuelto.</p>
        {apertura && (
          <div className="py-3">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-[15px] font-semibold text-ink">{apertura.titulo}</span>
              <ChipEstado estado={apertura.estado} />
            </div>
            <p className="mt-0.5 text-[13px] leading-relaxed text-ink-2">{apertura.ayuda}</p>
            <ul className="mt-1">
              {e.boticas.map((b) => (
                <FechaApertura key={`${b.sucursal_id}-${b.apertura ?? ""}`} b={b} hoy={e.hoy} onCambio={onCambio} />
              ))}
            </ul>
          </div>
        )}
        {e.marcas
          .filter((m) => m.clave !== "apertura")
          .map((m) => (
            <MarcaAMano key={m.clave} m={m} onCambio={onCambio} />
          ))}
      </Plegable>
    </section>
  );
}

// ---------- «¿Dónde está qué?» ----------
const lineasPara = (p: PantallaMapa | undefined, filtro: Filtro) =>
  (p?.lineas ?? []).filter((l) => filtro === "todo" || (filtro === "toca" ? l.tipo === "toca" : l.tipo === "construir"));

function FilaPantalla({ v, miRol, estado, filtro }: { v: Vista; miRol: Rol; estado: PantallaMapa | undefined | null; filtro: Filtro }) {
  const puede = v.roles.includes(miRol);
  const conEstado = estado !== null; // null = este rol no ve el estado
  return (
    <li className="flex flex-col gap-2 border-b border-line-row py-3 last:border-0 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span aria-hidden="true" className="text-[15px]">{v.icono}</span>
          <span className="text-[15px] font-semibold text-ink">{v.label}</span>
          {conEstado && <ChipEstado estado={estado?.estado ?? "ok"} />}
        </div>
        <p className="mt-0.5 text-[13px] leading-relaxed text-ink-2">{PARA_QUE[v.id]}</p>
        <p className="mt-0.5 text-[13px] text-ink-3">
          {laVen(v)} · Llegas por {llegasPor(v.id)}
        </p>
        {conEstado && <Lineas lineas={lineasPara(estado, filtro)} />}
      </div>
      {puede ? (
        <IrBoton id={v.id} etiqueta={v.label} className="w-full sm:w-auto sm:self-start" />
      ) : (
        <span className="flex-none text-[13px] text-ink-3">no aplica a tu rol</span>
      )}
    </li>
  );
}

// --- Vista A: por sección (las del sidebar + Punto de venta + red de seguridad) ---
function PorSeccion({ miRol, mapa, filtro }: { miRol: Rol; mapa: MapaEstado | null; filtro: Filtro }) {
  // Red de seguridad anti-desfasaje: cualquier vista que no esté en SECCIONES.owns ni en el POS
  // cae a "Otras pantallas" en vez de desaparecer del mapa.
  const cubiertas = new Set<RutaId>([...SECCIONES.flatMap((s) => s.owns), ...RUTAS_POS]);
  const pos = VISTAS.filter((v) => v.grupo === "pos").map((v) => v.id);
  const sueltas = VISTAS.filter((v) => !cubiertas.has(v.id) && v.grupo !== "pos").map((v) => v.id);
  const estadoDe = (id: RutaId): PantallaMapa | undefined | null => (mapa ? mapa.pantallas.find((p) => p.ruta === id) : null);
  // Con filtro, solo las filas que tienen algo de ese tipo.
  const visibles = (ids: RutaId[]) => (filtro === "todo" || !mapa ? ids : ids.filter((id) => lineasPara(estadoDe(id) ?? undefined, filtro).length > 0));

  const tarjeta = (clave: string, titulo: string, ids: RutaId[], nota?: string) => {
    const filas = visibles(ids);
    if (filas.length === 0) return null;
    return (
      <Card key={clave}>
        <SectionLabel>{titulo}</SectionLabel>
        {nota && <p className="mt-1 text-[13px] text-ink-3">{nota}</p>}
        <ul className="mt-1">
          {filas.map((id) => (
            <FilaPantalla key={id} v={vistaDe(id)} miRol={miRol} estado={estadoDe(id)} filtro={filtro} />
          ))}
        </ul>
      </Card>
    );
  };

  const sinPantalla = mapa && filtro !== "toca" ? mapa.falta_construir.filter((f) => f.ruta === null) : [];
  const tarjetas = [
    ...SECCIONES.map((s) => tarjeta(s.id, s.label, s.owns)),
    tarjeta("pos", "Punto de venta (celular / mostrador)", pos, "Lo que ve el vendedor en la botica. El encargado entra por «Mostrador» en el menú; en el celular estas pantallas se abren siempre en este formato."),
    sueltas.length > 0 ? tarjeta("sueltas", "Otras pantallas", sueltas) : null,
  ].filter(Boolean);

  return (
    <div className="grid grid-cols-1 items-start gap-3.5 lg:grid-cols-2">
      {tarjetas}
      {sinPantalla.length > 0 && (
        <Card>
          <SectionLabel>Sin pantalla</SectionLabel>
          <Lineas lineas={sinPantalla.map((f) => ({ tipo: "construir" as const, texto: f.texto }))} />
        </Card>
      )}
      {tarjetas.length === 0 && sinPantalla.length === 0 && <p className="text-[13px] text-ink-3">Nada con este filtro.</p>}
    </div>
  );
}

// --- Vista B: según tu rol (qué ve cada quien) ---
function PorRol({ miRol }: { miRol: Rol }) {
  const columnas: { rol: Rol; titulo: string; alcance: string }[] = [
    { rol: "operador", titulo: "Vendedor", alcance: "Solo el punto de venta de su botica" },
    { rol: "admin_sucursal", titulo: "Encargado", alcance: "Su botica: panel + mostrador" },
    { rol: "super_admin", titulo: "Dueño", alcance: "Toda la cadena" },
  ];
  return (
    <div className="grid grid-cols-1 items-start gap-3.5 lg:grid-cols-3">
      {columnas.map((c) => {
        const esMio = c.rol === miRol;
        const vistas = VISTAS.filter((v) => v.roles.includes(c.rol));
        return (
          // En el celular, la columna del propio rol va primero.
          <Card key={c.rol} className={cn(esMio && "border-accent max-lg:order-first")}>
            <div className="flex items-center justify-between gap-2">
              <SectionLabel>{c.titulo}</SectionLabel>
              {esMio && <Chip variant="ok">tu rol</Chip>}
            </div>
            <p className="mt-0.5 text-[13px] text-ink-3">{c.alcance}</p>
            <ul className="mt-1">
              {vistas.map((v) => (
                <li key={v.id} className="flex items-start gap-1.5 border-b border-line-row py-2 last:border-0">
                  <span aria-hidden="true" className="text-[13px]">{v.icono}</span>
                  <div className="min-w-0">
                    <span className="text-[13px] font-semibold text-ink">{v.label}</span>
                    <p className="text-[13px] leading-relaxed text-ink-2">{PARA_QUE[v.id]}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        );
      })}
    </div>
  );
}

export function Mapa({ sesion }: { sesion: SesionActiva }) {
  const [tab, setTab] = useState<"seccion" | "rol">("seccion");
  const [filtro, setFiltro] = useState<Filtro>("todo");
  const miRol = sesion.usuario.rol;
  const esDueno = miRol === "super_admin";
  // Solo el Dueño lo pide: a los demás roles el servidor les respondería 403, y los datos no viajan.
  const estado = useApi<MapaEstado>(esDueno ? "/mapa/estado" : null);
  const recargar = () => {
    estado.recargar();
    avisarCambioDelMapa();
  };
  const mapa = esDueno ? estado.data : null;
  const cuenta = (tipo: TipoLinea) => (mapa ? mapa.pantallas.filter((p) => p.lineas.some((l) => l.tipo === tipo)).length : 0);

  return (
    <div className="flex flex-col gap-[18px]">
      <div>
        <h2 className="text-[17px] font-bold tracking-[-0.01em] text-ink">¿Dónde está qué?</h2>
        <p className="mt-1 max-w-[640px] text-[13px] leading-relaxed text-ink-2">
          El plano de todo el sistema: cada sección, sus pantallas, para qué sirve cada una y quién la ve.
          Este mapa se arma solo desde el registro de rutas, así que siempre está al día.
        </p>
      </div>

      {esDueno &&
        (estado.error ? (
          <Card>
            <p className="text-[13px] text-accent-ink">No se pudo leer el estado del sistema: {estado.error}</p>
            <button type="button" onClick={estado.recargar} className="mt-2 min-h-[44px] self-start text-[13px] text-link underline">
              Reintentar
            </button>
          </Card>
        ) : !estado.data ? (
          <p className="text-[13px] text-ink-3">Leyendo qué está listo y qué falta…</p>
        ) : (
          <BloqueTesoro e={estado.data} onCambio={recargar} />
        ))}

      <div className="flex flex-col gap-2.5">
        <Tabs>
          <TabPill active={tab === "seccion"} onClick={() => setTab("seccion")}>
            Por sección
          </TabPill>
          <TabPill active={tab === "rol"} onClick={() => setTab("rol")}>
            Según tu rol
          </TabPill>
        </Tabs>
        {mapa && tab === "seccion" && (
          <div role="group" aria-label="Ver" className="flex flex-wrap items-center gap-1.5">
            <span className="text-[13px] text-ink-3">Ver:</span>
            {(
              [
                ["todo", "Todo"],
                ["toca", `Te toca a ti (${cuenta("toca")})`],
                ["construir", `Falta construir (${cuenta("construir")})`],
              ] as const
            ).map(([f, t]) => (
              <TabPill key={f} active={filtro === f} onClick={() => setFiltro(f)}>
                {t}
              </TabPill>
            ))}
          </div>
        )}
      </div>
      {tab === "seccion" ? <PorSeccion miRol={miRol} mapa={mapa} filtro={filtro} /> : <PorRol miRol={miRol} />}
    </div>
  );
}
